import { useCallback } from "react";
import { useI18n } from "@/hooks/useI18n";
import { useDrawers } from "@/hooks/useDrawers";
import { usePaginatedFlowers } from "@/hooks/usePaginatedFlowers";
import { SEOHead } from "@/components/SEOHead";
import { PaginatedFlowerFilters } from "@/components/catalog/PaginatedFlowerFilters";
import { FlowerCard } from "@/components/catalog/FlowerCard";
import { FlowerCardSkeleton } from "@/components/catalog/FlowerCardSkeleton";
import { LoadMoreButton } from "@/components/catalog/LoadMoreButton";
import { Button } from "@/components/ui/button";
import heroImageWebp from "@/assets/hero-flowers-optimized.webp";

export default function Catalog() {
  const { t } = useI18n();
  const { 
    flowers, 
    isLoading, 
    isLoadingMore,
    error, 
    totalCount,
    currentlyRendered,
    remaining,
    hasMore,
    isAllLoaded,
    filters,
    filterOptions,
    updateFilters,
    loadMore,
    retry,
    isReady 
  } = usePaginatedFlowers();

  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters: { types: string[], colors: string[], searchTerm?: string }) => {
    updateFilters(newFilters);
  }, [updateFilters]);

  // Define a common type for regular categories (not premium cards)
  type RegularCategory = {
    name: string;
    varieties: string[];
    image: string;
    description: string;
  };

  // Regular catalog categories (keeping i18n for these)
  const regularCategories: RegularCategory[] = [
    {
      name: t('catalog.products.gypsophila.name'),
      varieties: [
        t('catalog.products.gypsophila.varieties.xlence'),
        t('catalog.products.gypsophila.varieties.mirabella'),
        t('catalog.products.gypsophila.varieties.excellence')
      ],
      image: "/placeholder.svg",
      description: t('catalog.products.gypsophila.desc')
    },
    {
      name: t('catalog.products.alstroemeria.name'),
      varieties: [
        t('catalog.products.alstroemeria.varieties.assorted'),
        t('catalog.products.alstroemeria.varieties.white'),
        t('catalog.products.alstroemeria.varieties.pink'),
        t('catalog.products.alstroemeria.varieties.purple')
      ],
      image: "/placeholder.svg",
      description: t('catalog.products.alstroemeria.desc')
    },
    {
      name: t('catalog.products.chrysanthemums.name'),
      varieties: [
        t('catalog.products.chrysanthemums.varieties.button'),
        t('catalog.products.chrysanthemums.varieties.spray'),
        t('catalog.products.chrysanthemums.varieties.disbud')
      ],
      image: "/placeholder.svg",
      description: t('catalog.products.chrysanthemums.desc')
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Flower Catalog - Premium Ecuadorian Flowers"
        description="Explore our complete selection of premium Ecuadorian flowers available for immediate quotation and sampling."
      />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-andean text-andean-cream relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(${heroImageWebp})` }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-display font-light mb-6 tracking-wide animate-fade-up">
              Flower Catalog
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-andean-cream/90 animate-fade-up" style={{animationDelay: '0.2s'}}>
              Explore our complete selection of premium Ecuadorian flowers, available for immediate quotation and sampling
            </p>
          </div>
        </div>
      </section>


      {/* Flowers Collection */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-display font-light text-foreground mb-4 tracking-wide">
              Our Flower Collection
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Discover our complete selection of premium Ecuadorian flowers
            </p>
          </div>

          {/* Filters - Solo mostrar cuando esté listo */}
          {isReady && !error && filterOptions.availableTypes.length > 0 && (
            <div className="max-w-7xl mx-auto">
              <PaginatedFlowerFilters 
                filterOptions={filterOptions}
                currentFilters={filters}
                onFiltersChange={handleFiltersChange}
                totalCount={totalCount}
                currentlyRendered={currentlyRendered}
              />
            </div>
          )}

          {/* Loading State with Skeletons - Solo mostrar si realmente estamos cargando */}
          {isLoading && flowers.length === 0 && (
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
                {Array.from({ length: 12 }).map((_, index) => (
                  <div key={index}>
                    <FlowerCardSkeleton />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-20">
              <div className="bg-card border border-destructive/20 rounded-lg p-8 max-w-md mx-auto">
                <p className="text-destructive mb-4 font-medium">{error}</p>
                <Button 
                  onClick={retry}
                  variant="outline"
                  className="mt-2"
                >
                  Retry
                </Button>
              </div>
            </div>
          )}

          {/* Empty State - Solo mostrar si estamos listos y no hay resultados */}
          {isReady && !error && flowers.length === 0 && totalCount === 0 && (
            <div className="text-center py-20">
              <div className="bg-card border border-border rounded-lg p-12 max-w-md mx-auto shadow-soft">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-display font-light text-foreground mb-2">No flowers match your filters</h3>
                <p className="text-muted-foreground">Try adjusting your search criteria</p>
              </div>
            </div>
          )}

          {/* Flowers Collection Grid - Solo mostrar cuando esté listo */}
          {isReady && !error && flowers.length > 0 && (
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
                {flowers.map((flower, index) => (
                  <div key={flower.id}>
                    <FlowerCard 
                      flower={flower} 
                      priority={index < 8} // Priority for first 8 images (above the fold)
                    />
                  </div>
                ))}
              </div>
              
              {/* Load More Button */}
              <LoadMoreButton
                remaining={remaining}
                isLoadingMore={isLoadingMore}
                isAllLoaded={isAllLoaded}
                hasMore={hasMore}
                onLoadMore={loadMore}
              />
            </div>
          )}
        </div>
      </section>

    </div>
  );
}